import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { noop } from '@cbhq/cds-utils';
import { Button } from '@cbhq/cds-web/buttons';
import { ListCell } from '@cbhq/cds-web/cells';
import { NativeTextArea, TextInput } from '@cbhq/cds-web/controls';
import { VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@cbhq/cds-web/overlays';
import { TextLabel1 } from '@cbhq/cds-web/typography';

import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import useWebhooksURLValidation from ':cloud/hooks/useWebhooksURLValidation';
import { CoinbaseWebhook } from ':cloud/types/ts_types';
import { addressTooltipContent } from ':cloud/widgets/webhooks/CoinbaseWebhookCreateModal';
import { WebhookFiltersTitle } from ':cloud/widgets/webhooks/components/WebhookFiltersTitle';

import {
  getEventType,
  getEventTypeProps,
  getNetwork,
  getStatus,
  validateEthereumAddress,
} from '../helpers';

type CoinbaseWebhookEditFieldsModalProps = {
  webhook: CoinbaseWebhook;
  onRequestClose: () => void;
  onSubmit: (updatedWebhook: CoinbaseWebhook) => void;
};

export function CoinbaseWebhookEditFieldsModal({
  webhook,
  onRequestClose,
  onSubmit,
}: CoinbaseWebhookEditFieldsModalProps) {
  const [webhookURL, setWebhookURL] = useState(webhook.notificationUri);
  const [rawAddresses, setRawAddresses] = useState(
    webhook?.eventTypeFilter?.walletActivityFilter.addresses
      ? webhook?.eventTypeFilter?.walletActivityFilter.addresses.join(',')
      : '',
  );
  const [addresses, setAddresses] = useState<string[] | undefined>(
    webhook?.eventTypeFilter?.walletActivityFilter.addresses,
  );
  const [contractAddress, setContractAddress] = useState(
    webhook?.eventFilters?.[0]?.contractAddress,
  );
  const [fromAddress, setFromAddress] = useState(webhook?.eventFilters?.[0].fromAddress);
  const [toAddress, setToAddress] = useState(webhook?.eventFilters?.[0].toAddress);
  const [selectedNetwork] = useState(webhook.network);
  const [contractAddressError, setContractAddressError] = useState('');
  const [fromAddressError, setFromAddressError] = useState('');
  const [toAddressError, setToAddressError] = useState('');
  const [addressesError, setAddressesError] = useState('');

  const { urlError, validateUrl } = useWebhooksURLValidation();
  const { isPhone } = useSimpleBreakpoints();

  const { isWalletActivity } = getEventTypeProps(webhook.eventType);

  const handleOnChangeURL = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const url = event.target.value.trim();
      setWebhookURL(url);
      validateUrl(url);
    },
    [validateUrl],
  );

  const handleOnChangeAddresses = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    const newAddressesRaw = event.target.value.trim();
    setRawAddresses(newAddressesRaw);

    // validate addresses
    let newAddresses = newAddressesRaw.split(',');
    newAddresses = newAddresses.filter((addr) => addr.length > 0);
    for (const addr of newAddresses) {
      if (!validateEthereumAddress(addr)) {
        setAddressesError('One or more addresses entered are invalid.');
        break;
      } else {
        setAddressesError('');
      }
    }
    setAddresses(newAddresses);
  }, []);

  const handleOnChangeContractAddress = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const address = event.target.value.trim();
      setContractAddress(address);
      if (address && !validateEthereumAddress(address)) {
        setContractAddressError(
          'Please enter a valid Ethereum address (40 hexadecimal characters starting with 0x)',
        );
      } else {
        setContractAddressError('');
      }
    },
    [],
  );

  const handleOnChangeFromAddress = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const address = event.target.value.trim();
    setFromAddress(address);
    if (address && !validateEthereumAddress(address)) {
      setFromAddressError(
        'Please enter a valid Ethereum address (40 hexadecimal characters starting with 0x)',
      );
    } else {
      setFromAddressError('');
    }
  }, []);

  const handleOnChangeToAddress = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const address = event.target.value.trim();
    setToAddress(address);
    if (address && !validateEthereumAddress(address)) {
      setToAddressError(
        'Please enter a valid Ethereum address (40 hexadecimal characters starting with 0x)',
      );
    } else {
      setToAddressError('');
    }
  }, []);

  const handleSubmit = useCallback(() => {
    validateUrl(webhookURL);

    if (urlError) {
      return;
    }

    const updatedWebhook: CoinbaseWebhook = {
      ...webhook,
      network: selectedNetwork,
      eventType: webhook.eventType,
      notificationUri: webhookURL,
      ...(isWalletActivity
        ? {
            eventTypeFilter: {
              walletActivityFilter: {
                addresses,
              },
            },
          }
        : {
            eventFilters: [
              {
                contractAddress,
                fromAddress,
                toAddress,
              },
            ],
          }),
    };

    onSubmit(updatedWebhook);
  }, [
    webhookURL,
    urlError,
    webhook,
    selectedNetwork,
    contractAddress,
    fromAddress,
    toAddress,
    addresses,
    validateUrl,
    onSubmit,
    isWalletActivity,
  ]);

  const addressesSection = useMemo(() => {
    if (isWalletActivity) {
      let addressesCount = 0;
      if (addresses?.length && typeof addresses?.length === 'number') {
        addressesCount = addresses.length;
      }
      return (
        <TextInput
          inputNode={
            <NativeTextArea
              value={rawAddresses}
              onChange={handleOnChangeAddresses}
              rows={5}
              cols={5}
              placeholder="Enter the wallet addresses here"
            />
          }
          helperText={addressesError || `${addressesCount} address(es)`}
          label="Wallet addresses (separated by comma)"
          variant={addressesError ? 'negative' : undefined}
        />
      );
    }
    return (
      <>
        <TextLabel1 as="p">
          {addressTooltipContent}
          <br />
          At least one of the following addresses is required to create a webhook.
        </TextLabel1>
        <TextInput
          label="Contract Address"
          required
          placeholder="0xdeadbeef"
          value={contractAddress}
          onChange={handleOnChangeContractAddress}
          helperText={contractAddressError}
          variant={contractAddressError ? 'negative' : undefined}
        />
        <TextInput
          label="From Address"
          required
          placeholder="0xdeadbeef"
          value={fromAddress}
          onChange={handleOnChangeFromAddress}
          helperText={fromAddressError}
          variant={fromAddressError ? 'negative' : undefined}
        />
        <TextInput
          label="To Address"
          required
          placeholder="0xdeadbeef"
          value={toAddress}
          onChange={handleOnChangeToAddress}
          helperText={toAddressError}
          variant={toAddressError ? 'negative' : undefined}
        />
      </>
    );
  }, [
    isWalletActivity,
    contractAddress,
    contractAddressError,
    handleOnChangeContractAddress,
    fromAddress,
    fromAddressError,
    handleOnChangeFromAddress,
    toAddress,
    toAddressError,
    handleOnChangeToAddress,
    rawAddresses,
    handleOnChangeAddresses,
    addresses,
    addressesError,
  ]);

  return (
    <Modal onRequestClose={onRequestClose || noop} visible width={isPhone ? undefined : 480}>
      <ModalHeader title="Edit Webhook Details" />
      <ModalBody>
        <VStack gap={2}>
          <TextInput
            label="Webhook URL"
            required
            placeholder="https://example.com/"
            value={webhookURL}
            onChange={handleOnChangeURL}
            helperText={urlError}
            variant={urlError ? 'negative' : undefined}
          />
          <ListCell description="Status" detail={getStatus(webhook.webhookStatus)} />
          <ListCell description="Network Type" detail={getNetwork(webhook.network)} />
          <ListCell description="Event Type" detail={getEventType(webhook.eventType)} />
          <WebhookFiltersTitle />
          {addressesSection}
        </VStack>
      </ModalBody>
      <ModalFooter
        secondaryAction={
          <Button variant="secondary" onPress={onRequestClose}>
            Cancel
          </Button>
        }
        primaryAction={
          <Button
            type="submit"
            onPress={handleSubmit}
            testID="submit"
            disabled={
              !webhookURL || (!contractAddress && !fromAddress && !toAddress && !addresses?.length)
            }
          >
            Save
          </Button>
        }
      />
    </Modal>
  );
}
